import { Opportunity as OpportunityProto } from '@taxfyle/api-internal/internal/opportunity_pb'
import type {
  OpportunityConsultation,
  OpportunityInfoLine,
  OpportunityLegendDescriptor,
  OpportunityScopeItem,
  RoutingMode,
} from './Opportunity'
import { type InventoryItemType } from '@/features/inventory/InventoryItem'
import { TimeRange } from '@/infra/time-range'
import { durationToMinutes, timestampToDate } from '@/utils/grpc-util'
import { match } from '@/utils/pattern-match-util'
import type { Interval } from '@taxfyle/api-internal/shared/types/interval_pb'

/**
 * Maps the legend an opportunity's job is for.
 */
export function mapOpportunityLegendProtoToLegend(
  proto: OpportunityProto.Legend.AsObject,
): OpportunityLegendDescriptor {
  return {
    id: proto.id,
    version: proto.version,
    name: proto.name,
    priceUnit: proto.priceUnit?.value,
  }
}

/**
 * Maps a line of an opportunity's details.
 */
export function mapOpportunityInfoLineProtoToInfoLine(
  proto: OpportunityProto.InfoLine.AsObject,
): OpportunityInfoLine {
  return { title: proto.title, content: proto.content }
}

/**
 * Maps an item in an opportunity's scope.
 */
export function mapOpportunityScopeItemProtoToScopeItem(
  proto: OpportunityProto.JobScopeItem.AsObject,
): OpportunityScopeItem {
  return {
    name: proto.name,
    quantity: proto.quantity,
    inventoryItemType: mapInventoryItemTypeProtoToInventoryItemType(
      proto.inventoryItemType,
    ),
  }
}

/**
 * Maps an opportunity's consultation, splitting the client's availability
 * into slots of the requested length.
 */
export function mapOpportunityConsultationProtoToConsultation(
  proto: OpportunityProto.Consultation.AsObject,
): OpportunityConsultation {
  const requestedLength = proto.requestedLength
    ? durationToMinutes(proto.requestedLength)
    : 30
  return {
    consultationId: proto.id,
    availability: proto.availabilityTimesList
      .map(mapIntervalProtoToTimeRange)
      .flatMap((a) => a.decomposeRangeInIntervals(requestedLength)),
    requestedLength,
  }
}

/**
 * Maps an interval to a time range.
 */
export function mapIntervalProtoToTimeRange(proto: Interval.AsObject) {
  return TimeRange.of(
    timestampToDate(proto.startTime) ?? new Date(),
    timestampToDate(proto.endTime) ?? new Date(),
  )
}

/**
 * Maps how an opportunity is routed to pros.
 */
export function mapRoutingModeProtoToRoutingMode(
  proto: OpportunityProto.RoutingMode,
): RoutingMode {
  switch (proto) {
    case OpportunityProto.RoutingMode.FIRST_COME_FIRST_SERVED:
      return 'FirstComeFirstServed'
    case OpportunityProto.RoutingMode.DISPATCH:
      return 'Dispatch'
    case OpportunityProto.RoutingMode.UNSPECIFIED:
      throw new Error('Unreachable')
  }
}

function mapInventoryItemTypeProtoToInventoryItemType(
  proto: OpportunityProto.JobScopeInventoryItemType,
): InventoryItemType {
  return match(proto, {
    [OpportunityProto.JobScopeInventoryItemType.BOOKKEEPING]: 'Bookkeeping',
    [OpportunityProto.JobScopeInventoryItemType.CONSULTATION]: 'Consultation',
    [OpportunityProto.JobScopeInventoryItemType.TAX]: 'Tax',
    [OpportunityProto.JobScopeInventoryItemType.DEFAULT]: 'Default',
  })
}
